import React from "react";
import { IconButton, Tooltip, Typography, Box, Badge } from "@mui/material";
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  NotificationsActive as NotificationsActiveIcon,
} from "@mui/icons-material";
import { useNotifications } from "../../context/NotificationContext";

/**
 * NotificationButton Component
 * Displays notification status and allows enabling/disabling notifications
 */
const NotificationButton = ({ showLabel = true, size = "medium" }) => {
  const {
    permissionStatus,
    requestNotificationPermission,
    unreadCount,
    isSupported,
  } = useNotifications();

  if (!isSupported) {
    return null; // Don't show if browser doesn't support notifications
  }

  const getIcon = () => {
    if (permissionStatus === "granted") {
      return unreadCount > 0 ? (
        <NotificationsActiveIcon />
      ) : (
        <NotificationsIcon />
      );
    }
    return <NotificationsOffIcon />;
  };

  const getColor = () => {
    if (permissionStatus === "granted") {
      return unreadCount > 0 ? "secondary" : "success";
    }
    return "default";
  };

  const getTooltip = () => {
    if (permissionStatus === "granted") {
      return unreadCount > 0
        ? `${unreadCount} unread notifications`
        : "Notifications enabled";
    }
    if (permissionStatus === "denied") {
      return "Notifications blocked - check browser settings";
    }
    return "Click to enable notifications";
  };

  const getLabel = () => {
    if (permissionStatus === "granted") {
      return unreadCount > 0 ? `${unreadCount} New` : "Notifications On";
    }
    if (permissionStatus === "denied") {
      return "Notifications Blocked";
    }
    return "Enable Notifications";
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip title={getTooltip()}>
        <IconButton
          onClick={requestNotificationPermission}
          color={getColor()}
          disabled={permissionStatus === "denied"}
          size={size}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {getIcon()}
          </Badge>
        </IconButton>
      </Tooltip>
      {showLabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            minWidth: size === "small" ? "60px" : "120px",
            fontSize: size === "small" ? "0.7rem" : "0.75rem",
          }}
        >
          {getLabel()}
        </Typography>
      )}
    </Box>
  );
};

export default NotificationButton;
